import { useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { getPlans } from '@queries'
import { updatePlan } from '@mutations'
import { usePlanPdfViewer } from '../PlanPdfViewerContext'

/**
 * Saves the PDF edited in the "Pläne" tab's preview as the plan's new PDF (the existing plan
 * update route replaces the stored file; name, comment and tags are sent unchanged).
 */
const useSavePlanPdf = () => {
  const { projectId } = useParams()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { viewer, setChangedDocument, setLoadedDocument } = usePlanPdfViewer()
  const plansKey = getPlans.getKey(projectId as string)

  // Shared key: every user of this hook (the panel's "Speichern", the plan switch) sees a running
  // save, whichever of them started it.
  const mutationKey = ['savePlanPdf', projectId]
  const isSaving = useIsMutating(mutationKey) > 0

  const { mutateAsync } = useMutation(
    async (plan: Plan): Promise<Plan> => {
      // Exporting the edited document takes a moment too, so it belongs to the save.
      const file = await viewer?.getBlobCurrentDocument()
      if (!file) throw new Error('Der angepasste Plan konnte nicht gelesen werden')
      return updatePlan.request(projectId as string, {
        guid: plan.guid,
        title: plan.node_name,
        comment: plan.comment ?? '',
        tags: plan.tags ?? [],
        file,
      })
    },
    {
      mutationKey,
      onSuccess: updatedPlan => {
        setChangedDocument(null)
        // The stored PDF changed: let the preview load the saved file from its new url.
        setLoadedDocument(null)
        queryClient.setQueryData<Plan[]>(plansKey, plans =>
          plans?.map(plan => (plan.guid === updatedPlan.guid ? updatedPlan : plan)),
        )
        queryClient.invalidateQueries(plansKey)
        enqueueSnackbar('Plan wurde gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError | Error) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Plans'), {
          variant: 'error',
        })
      },
    },
  )

  /** Saves the preview's current document as `plan`'s PDF; resolves whether it succeeded. */
  const savePlanPdf = async (plan: Plan): Promise<boolean> => {
    // The saved file is read when the save starts, and the preview reloads it afterwards: edits
    // made in between would be lost silently, so the annotations are locked meanwhile.
    const annotationManager = viewer?.viewerInstance?.Core.annotationManager
    annotationManager?.enableReadOnlyMode()
    try {
      await mutateAsync(plan)
      return true
    } catch {
      // Reported by onError.
      return false
    } finally {
      annotationManager?.disableReadOnlyMode()
    }
  }

  return { savePlanPdf, isSaving }
}

export default useSavePlanPdf
